import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';

// i18n
import './i18n/config'; // Initialize i18n
import { LanguageProvider } from './i18n';

// Main property pages
import AtacamaPage from './pages/Atacama/AtacamaPage';
import PatagoniaPageHardcoded from './pages/Patagonia/PatagoniaPageHardcoded';
import IguazuPage from './pages/Iguazu/IguazuPage';
import MendozaPage from './pages/Mendoza/MendozaPage';
import SantaCatarinaPage from './pages/SantaCatarina/SantaCatarinaPage';
import AccommodationsDetail from './pages/AccommodationsDetail/AccommodationsDetail';


// Atacama sub-pages
import ExperiencesAtacama from './pages/Atacama/ExperiencesAtacama';
import GalleryAtacama from './pages/Atacama/GalleryAtacama';
import GettingHereAtacama from './pages/Atacama/GettingHereAtacama';
import ProducingNatureAtacama from './pages/Atacama/ProducingNatureAtacama';
import PackagesAtacama from './pages/Atacama/PackagesAtacama';
import AccommodationsDetailAtacama from './pages/Atacama/AccommodationsDetailAtacama';
import FilmCollectionAtacama from './pages/Atacama/FilmCollectionAtacama';
import ArtImmersionsAtacama from './pages/Atacama/ArtImmersionsAtacama';
import WellnessAtacama from './pages/Atacama/WellnessAtacama';
import AboutAtacama from './pages/Atacama/AboutAtacama';
import TheHotelAtacama from './pages/Atacama/TheHotelAtacama';
import DiningAtacama from './pages/Atacama/DiningAtacama';
import ContactAtacama from './pages/Atacama/ContactAtacama';
import BlogAtacama from './pages/Atacama/BlogAtacama';
import BlogDetailAtacama from './pages/Atacama/BlogDetailAtacama';

// Patagonia sub-pages
import ExperiencesPatagonia from './pages/Patagonia/ExperiencesPatagonia';
import GalleryPatagonia from './pages/Patagonia/GalleryPatagonia';
import GettingHerePatagonia from './pages/Patagonia/GettingHerePatagonia';
import MeetingsEventsPatagonia from './pages/Patagonia/MeetingsEventsPatagonia';
import AccommodationsDetailPatagonia from './pages/Patagonia/AccommodationsDetailPatagonia';
import DiningPatagonia from './pages/Patagonia/DiningPatagonia';

// Iguazu sub-pages
import ExperiencesIguazu from './pages/Iguazu/ExperiencesIguazu';
import GalleryIguazu from './pages/Iguazu/GalleryIguazu';
import GettingHereIguazu from './pages/Iguazu/GettingHereIguazu';
import MeetingsEventsIguazu from './pages/Iguazu/MeetingsEventsIguazu';
import DiningIguazu from './pages/Iguazu/DiningIguazu';

// Mendoza sub-pages
import ExperiencesMendoza from './pages/Mendoza/ExperiencesMendoza';
import GalleryMendoza from './pages/Mendoza/GalleryMendoza';
import GettingHereMendoza from './pages/Mendoza/GettingHereMendoza';
import MeetingsEventsMendoza from './pages/Mendoza/MeetingsEventsMendoza';
import DiningMendoza from './pages/Mendoza/DiningMendoza';

// Santa Catarina sub-pages
import ExperiencesSantaCatarina from './pages/SantaCatarina/ExperiencesSantaCatarina';
import GallerySantaCatarina from './pages/SantaCatarina/GallerySantaCatarina';
import GettingHereSantaCatarina from './pages/SantaCatarina/GettingHereSantaCatarina';
import MeetingsEventsSantaCatarina from './pages/SantaCatarina/MeetingsEventsSantaCatarina';
import DiningSantaCatarina from './pages/SantaCatarina/DiningSantaCatarina';

// Global pages
import GalleryPage from './pages/Gallery/GalleryPage';
import BlogPage from './pages/Blog/BlogPage';
import BlogDetailPage from './pages/Blog/BlogDetailPage';
import PackagesPage from './pages/Packages/PackagesPage';
import PackageDetailPage from './pages/PackageDetail/PackageDetailPage';
import OffersPage from './pages/Offers/OffersPage';
import OurStoryPage from './pages/OurStory/OurStoryPage';
import DestinationsPage from './pages/Destinations/DestinationsPage';
import ProducingNaturePage from './pages/ProducingNature/ProducingNaturePage';
import FilmCollectionPage from './pages/FilmCollection/FilmCollectionPage';
import ReEnergizePage from './pages/ReEnergize/ReEnergizePage';
import BrandHomePage from './pages/BrandHomePage/BrandHomePage';
import ArtImmersionsPage from './pages/ArtImmersions/ArtImmersionsPage';
import ArtImmersionDetailPage from './pages/ArtImmersions/ArtImmersionDetailPage';
import ContactPage from './pages/Contact/ContactPage';
import PressAwardsPage from './pages/PressAwards/PressAwardsPage';
import FactSheetPage from './pages/FactSheet/FactSheetPage';
import FAQPage from './pages/FAQ/FAQPage';
import CareersPage from './pages/Careers/CareersPage';
import AboutUsPage from './pages/AboutUs/AboutUsPage';
import PrivacyPolicyPage from './pages/Legal/PrivacyPolicyPage';
import TermsConditionsPage from './pages/Legal/TermsConditionsPage';
import SitemapPage from './pages/Sitemap/SitemapPage';
import NotFoundPage from './pages/NotFound/NotFoundPage';
import MeetingsGatherings from './pages/MeetingsGatherings/MeetingsGatherings';
import ExperiencesPage from './pages/Experiences/ExperiencesPage';


// Components
import MenuOverlay from './components/MenuOverlay/MenuOverlay';
import SkipLinks from './components/SkipLinks/SkipLinks';
import { OverlayProvider } from './contexts/OverlayContext';
import { temporaryRedirects } from './TemporaryRedirects';

// Styles
import './components/shared/shared.css';
import './App.css';

// Component to handle scroll-to-top on route change
function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  }, [location]);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  }, []);

  return null;
}

/**
 * Redirects to the same URL with a trailing slash added if the current path
 * does not have one. This is useful for ensuring that the URL in the browser
 * address bar is consistent, even if the user manually enters a URL without
 * a trailing slash.
 *
 * @returns A React element that redirects to the URL with a trailing slash added,
 * or null if the current URL already has a trailing slash.
 */
const AddTrailingSlash: React.FC = () => {
  const location = useLocation();

  if (location.pathname !== '/' && !location.pathname.endsWith('/')) {
    return <Navigate to={`${location.pathname}/`} replace />;
  }

  return null;
};

function App() {
  return (
    <Router>
      <AddTrailingSlash />
      <LanguageProvider>
        <OverlayProvider>
          <div className="app-container">
            <SkipLinks />
            <ScrollToTop />
            <Routes>
              {/* Language-prefixed routes - Include all supported languages */}
              {['en', 'es', 'pt', 'de', 'fr'].map(lang => (
                <React.Fragment key={lang}>
                  <Route path={`/${lang}`} element={<BrandHomePage />} />
                  <Route path={`/${lang}/atacama`} element={<AtacamaPage />} />
                  <Route path={`/${lang}/patagonia`} element={<PatagoniaPageHardcoded />} />
                  <Route path={`/${lang}/iguazu`} element={<IguazuPage />} />
                  <Route path={`/${lang}/mendoza`} element={<MendozaPage />} />
                  <Route path={`/${lang}/santa-catarina`} element={<SantaCatarinaPage />} />
                </React.Fragment>
              ))}

              {/* Home */}
              <Route path="/" element={<BrandHomePage />} />

              {/* Atacama Routes */}
              <Route path="/atacama" element={<AtacamaPage />} />
              <Route path="/atacama/experiences" element={<ExperiencesAtacama />} />
              <Route path="/atacama/gallery" element={<GalleryAtacama />} />
              <Route path="/atacama/getting-here" element={<GettingHereAtacama />} />
              <Route path="/atacama/producing-nature" element={<ProducingNatureAtacama />} />
              <Route path="/atacama/packages" element={<PackagesAtacama />} />
              <Route path="/atacama/accommodations" element={<AccommodationsDetail property='atacama' />} />
              <Route path="/atacama/accommodations/:roomType" element={<AccommodationsDetailAtacama />} />
              <Route path="/atacama/film-collection" element={<FilmCollectionAtacama />} />
              <Route path="/atacama/art-immersions" element={<ArtImmersionsAtacama />} />
              <Route path="/atacama/wellness" element={<WellnessAtacama />} />
              <Route path="/atacama/about" element={<AboutAtacama />} />
              <Route path="/atacama/hotel" element={<TheHotelAtacama />} />
              <Route path="/atacama/dining" element={<DiningAtacama />} />
              <Route path="/atacama/contact" element={<ContactAtacama />} />
              <Route path="/atacama/blog" element={<BlogAtacama />} />
              <Route path="/atacama/blog/:slug" element={<BlogDetailAtacama />} />
              {/* Note: FAQ will link to existing global page */}

              {/* Patagonia Routes */}
              <Route path="/patagonia" element={<PatagoniaPageHardcoded />} />
              <Route path="/patagonia/experiences" element={<ExperiencesPatagonia />} />
              <Route path="/patagonia/gallery" element={<GalleryPatagonia />} />
              <Route path="/patagonia/getting-here" element={<GettingHerePatagonia />} />
              <Route path="/patagonia/meetings-events" element={<MeetingsEventsPatagonia />} />
              <Route path="/patagonia/accommodations" element={<AccommodationsDetail property='patagonia' />} />
              <Route path="/patagonia/accommodations/:roomType" element={<AccommodationsDetailPatagonia />} />
              <Route path="/patagonia/dining" element={<DiningPatagonia />} />

              {/* Iguazu Routes */}
              <Route path="/iguazu" element={<IguazuPage />} />
              <Route path="/iguazu/experiences" element={<ExperiencesIguazu />} />
              <Route path="/iguazu/gallery" element={<GalleryIguazu />} />
              <Route path="/iguazu/getting-here" element={<GettingHereIguazu />} />
              <Route path="/iguazu/meetings-events" element={<MeetingsEventsIguazu />} />
              <Route path="/iguazu/accommodations" element={<AccommodationsDetail property='iguazu' />} />
              <Route path="/iguazu/dining" element={<DiningIguazu />} />

              {/* Mendoza Routes */}
              <Route path="/mendoza" element={<MendozaPage />} />
              <Route path="/mendoza/experiences" element={<ExperiencesMendoza />} />
              <Route path="/mendoza/gallery" element={<GalleryMendoza />} />
              <Route path="/mendoza/getting-here" element={<GettingHereMendoza />} />
              <Route path="/mendoza/meetings-events" element={<MeetingsEventsMendoza />} />
              <Route path="/mendoza/accommodations" element={<AccommodationsDetail property='mendoza' />} />
              <Route path="/mendoza/dining" element={<DiningMendoza />} />

              {/* Santa Catarina Routes */}
              <Route path="/santa-catarina" element={<SantaCatarinaPage />} />
              <Route path="/santa-catarina/experiences" element={<ExperiencesSantaCatarina />} />
              <Route path="/santa-catarina/gallery" element={<GallerySantaCatarina />} />
              <Route path="/santa-catarina/getting-here" element={<GettingHereSantaCatarina />} />
              <Route path="/santa-catarina/meetings-events" element={<MeetingsEventsSantaCatarina />} />
              <Route path="/santa-catarina/accommodations" element={<AccommodationsDetail property='santa-catarina' />} />
              <Route path="/santa-catarina/dining" element={<DiningSantaCatarina />} />

              {/* Global Routes */}
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogDetailPage />} />
              <Route path="/packages" element={<PackagesPage />} />
              <Route path="/packages/:packageId" element={<PackageDetailPage />} />
              <Route path="/offers" element={<OffersPage />} />
              <Route path="/offers/:offerId" element={<PackageDetailPage />} />
              <Route path="/our-story" element={<OurStoryPage />} />
              <Route path="/destinations" element={<DestinationsPage />} />
              <Route path="/producing-nature" element={<ProducingNaturePage />} />
              <Route path="/film-collection" element={<FilmCollectionPage />} />
              <Route path="/re-energize" element={<ReEnergizePage />} />
              <Route path="/art-immersions" element={<ArtImmersionsPage />} />
              <Route path="/art-immersions/:id" element={<ArtImmersionDetailPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/press-awards" element={<PressAwardsPage />} />
              <Route path="/fact-sheet" element={<FactSheetPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-conditions" element={<TermsConditionsPage />} />
              <Route path="/sitemap" element={<SitemapPage />} />
              <Route path="/meetings-gatherings" element={<MeetingsGatherings />} />
              <Route path="/experiences" element={<ExperiencesPage />} />

              {/* Redirect routes for URL variations */}
              <Route path="/santa catarina" element={<Navigate to="/santa-catarina" replace />} />

              { /* Temporary Redirects */}
              {
                temporaryRedirects.map((redirect, index) => (
                  <Route
                    key={index}
                    path={redirect.originalPath}
                    element={<Navigate to={redirect.newPath} replace />}
                  />
                ))
              }


              {/* Catch-all route for 404s */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <MenuOverlay />
          </div>
        </OverlayProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
// testing